/**
 * Modal component for displaying track selection and race details
 */

import { getIntermissionsFrom, getYouTubeEmbedUrl, getIntermissionById, getTrackById } from '../utils/data.js';

let currentModal = null;
let currentTrack = null;

/**
 * Show unified track modal with selection and details
 * @param {Object} track - Track object from data
 */
export function showTrackSelectionModal(track) {
  // Get track with resolved shortcuts
  const resolvedTrack = getTrackById(track.id);
  if (!resolvedTrack) return;

  currentTrack = resolvedTrack;
  const intermissions = getIntermissionsFrom(track.id);

  // Default to showing standard race details (with resolved shortcuts)
  const defaultRaceData = resolvedTrack.standardRace;
  const defaultRaceName = resolvedTrack.name;

  const modalHtml = `
    <div class="modal-backdrop" id="modal-backdrop">
      <div class="modal-content-unified">
        <!-- Close Button -->
        <button class="modal-close-btn" onclick="window.closeModal()">
          &times;
        </button>

        <!-- Mobile Header with Dropdown (hidden on desktop) -->
        <div class="modal-mobile-header">
          <img
            id="modal-thumbnail-mobile"
            src="/thumbnails/${track.thumbnail}"
            alt="${track.name}"
            class="modal-mobile-thumbnail"
            onerror="this.style.display='none'"
          />
          <select
            id="race-selector-mobile"
            class="race-selector-dropdown"
            onchange="window.handleMobileRaceChange(this.value, '${track.id}')"
          >
            <option value="standard" selected>${track.name}</option>
            ${intermissions.map(intermission => `
              <option value="${intermission.id}">&raquo;&raquo;&raquo; ${intermission.toName}</option>
            `).join('')}
          </select>
        </div>

        <!-- Left Sidebar: Selection (hidden on mobile) -->
        <div class="modal-sidebar">
          <!-- Track Thumbnail -->
          <div class="mb-4">
            <img
              id="modal-thumbnail"
              src="/thumbnails/${track.thumbnail}"
              alt="${track.name}"
              class="w-full rounded-lg shadow-lg"
              onerror="this.style.display='none'"
            />
          </div>

          <!-- Track Selection List -->
          <div class="race-selection-list">
            <!-- Standard Track Option -->
            <button
              id="race-option-standard"
              class="race-option race-option-selected"
              onclick="window.selectRace('${track.id}', 'standard')"
            >
              ${track.name}
            </button>

            <!-- Intermission Options -->
            ${intermissions.map(intermission => `
              <button
                id="race-option-${intermission.id}"
                class="race-option race-option-intermission"
                onclick="window.selectRace('${track.id}', 'intermission', '${intermission.id}')"
              >
                &raquo;&raquo;&raquo; ${intermission.toName}
              </button>
            `).join('')}
          </div>
        </div>

        <!-- Right Panel: Race Details -->
        <div class="modal-details-pane" id="race-details-pane">
          ${renderRaceDetails(defaultRaceName, defaultRaceData)}
        </div>
      </div>
    </div>
  `;

  showModal(modalHtml);
}

/**
 * Render race details HTML
 * @param {string} raceName - Display name for the race
 * @param {Object} raceData - Race data object
 * @returns {string} HTML string for race details
 */
function renderRaceDetails(raceName, raceData) {
  return `
    <h2 class="text-2xl font-bold mb-6">${raceName}</h2>

    <!-- Race Details Grid -->
    <div class="grid grid-cols-2 gap-6 mb-6">
      <div class="detail-section">
        <div class="detail-label">Laps</div>
        <div class="detail-value">${raceData.laps}</div>
      </div>

      <div class="detail-section">
        <div class="detail-label">Strategy</div>
        <div class="detail-value">${raceData.strategy}</div>
      </div>

      ${raceData.vehicles ? `
        <div class="detail-section">
          <div class="detail-label">Special Vehicles</div>
          <div class="detail-value">${raceData.vehicles.join(', ')}</div>
        </div>
      ` : ''}

      ${raceData.variant && raceData.variant !== 'Standard' ? `
        <div class="detail-section">
          <div class="detail-label">Variant</div>
          <div class="detail-value">${raceData.variant}</div>
        </div>
      ` : ''}
    </div>

    <!-- Summary (optional) -->
    ${raceData.summary ? `
      <div class="detail-section">
        <div class="detail-label">Summary</div>
        <div class="detail-value text-base leading-relaxed">
          ${raceData.summary}
        </div>
      </div>
    ` : ''}

    <!-- Shortcuts -->
    ${(raceData.intermissionShortcuts && raceData.intermissionShortcuts.length > 0) || (raceData.shortcuts && raceData.shortcuts.length > 0) ? `
      <div class="detail-section">
        <div class="detail-label mb-3">Shortcuts</div>

        ${/* Render intermission-specific shortcuts if this is an intermission */ ''}
        ${raceData.intermissionShortcuts ?
          raceData.intermissionShortcuts.map((shortcut, index) => renderShortcut(shortcut, `intermission-${index}`)).join('')
          :
          /* For standard races, render all shortcuts */
          raceData.shortcuts.map((shortcut, index) => renderShortcut(shortcut, `shortcut-${index}`)).join('')
        }

        ${/* Show inherited shortcuts toggle for intermissions */ ''}
        ${raceData.inheritedShortcuts && raceData.inheritedShortcuts.length > 0 ? `
          <div class="mt-6 pt-6 border-t border-gray-700">
            <button
              onclick="window.toggleInheritedShortcuts()"
              class="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
            >
              <span id="inherited-shortcuts-icon">▼</span>
              <span>Show ${raceData.destinationTrackName} shortcuts</span>
            </button>
            <div id="inherited-shortcuts" class="hidden mt-4">
              ${raceData.inheritedShortcuts.map((shortcut, index) => renderShortcut(shortcut, `inherited-${index}`)).join('')}
            </div>
          </div>
        ` : ''}
      </div>
    ` : ''}
  `;
}

/**
 * Render a single shortcut
 * @param {Object} shortcut - Shortcut object
 * @param {string} id - Unique ID for this shortcut instance
 * @returns {string} HTML string for the shortcut
 */
function renderShortcut(shortcut, id) {
  return `
    <div class="mb-6 last:mb-0">
      <h4 class="text-lg font-semibold mb-2">${shortcut.name}</h4>
      ${shortcut.description ? `
        <p class="text-gray-300 mb-3">${shortcut.description}</p>
      ` : ''}
      ${shortcut.video ? `
        <button
          onclick="window.toggleVideo('video-${id}')"
          class="px-4 py-2 bg-mario-red hover:bg-red-600 rounded-lg transition-colors mb-3 flex items-center gap-2"
        >
          <span id="video-${id}-icon">▶</span>
          <span id="video-${id}-text">Show Video</span>
        </button>
        <div id="video-${id}" class="shortcut-video video-hidden">
          <iframe
            width="100%"
            height="100%"
            src="${getYouTubeEmbedUrl(shortcut.video, shortcut.videoStart, shortcut.videoEnd)}"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      ` : ''}
      ${shortcut.tweet ? `
        <button
          onclick="window.toggleTweet('tweet-${id}')"
          class="px-4 py-2 bg-mario-red hover:bg-red-600 rounded-lg transition-colors mb-3 flex items-center gap-2"
        >
          <span id="tweet-${id}-icon">▶</span>
          <span id="tweet-${id}-text">Show Video</span>
        </button>
        <div id="tweet-${id}" class="shortcut-tweet hidden">
          <blockquote class="twitter-tweet" data-theme="dark">
            <a href="${shortcut.tweet}"></a>
          </blockquote>
        </div>
      ` : ''}
    </div>
  `;
}

/**
 * Select a race and update the details pane
 * @param {string} trackId - Track ID
 * @param {string} type - 'standard' or 'intermission'
 * @param {string} intermissionId - Intermission ID (if type is 'intermission')
 */
export function selectRace(trackId, type, intermissionId = null) {
  // Get track with resolved shortcuts
  const track = getTrackById(trackId);
  if (!track) return;

  let raceData;
  let raceName;
  let selectedId;
  let thumbnailTrack = track; // Default to source track

  if (type === 'standard') {
    raceData = track.standardRace;
    raceName = track.name;
    selectedId = 'race-option-standard';
  } else {
    // Get intermission data with resolved shortcuts
    const intermission = getIntermissionById(intermissionId);
    if (!intermission) return;

    raceData = intermission;
    raceName = `${track.name} >>> ${intermission.toName}`;
    selectedId = `race-option-${intermissionId}`;

    // Use destination track's thumbnail for intermissions
    const destinationTrack = getTrackById(intermission.to);
    if (destinationTrack) {
      thumbnailTrack = destinationTrack;
    }
  }

  // Update the desktop thumbnail
  const thumbnailImg = document.getElementById('modal-thumbnail');
  if (thumbnailImg && thumbnailTrack.thumbnail) {
    thumbnailImg.src = `/thumbnails/${thumbnailTrack.thumbnail}`;
    thumbnailImg.alt = thumbnailTrack.name;
    thumbnailImg.style.display = ''; // Reset display in case it was hidden
  }

  // Update the mobile thumbnail
  const thumbnailImgMobile = document.getElementById('modal-thumbnail-mobile');
  if (thumbnailImgMobile && thumbnailTrack.thumbnail) {
    thumbnailImgMobile.src = `/thumbnails/${thumbnailTrack.thumbnail}`;
    thumbnailImgMobile.alt = thumbnailTrack.name;
    thumbnailImgMobile.style.display = ''; // Reset display in case it was hidden
  }

  // Update the mobile dropdown
  const mobileSelector = document.getElementById('race-selector-mobile');
  if (mobileSelector) {
    mobileSelector.value = type === 'standard' ? 'standard' : intermissionId;
  }

  // Update the details pane
  const detailsPane = document.getElementById('race-details-pane');
  if (detailsPane) {
    detailsPane.innerHTML = renderRaceDetails(raceName, raceData);
  }

  // Update selected state in sidebar (desktop)
  document.querySelectorAll('.race-option').forEach(btn => {
    btn.classList.remove('race-option-selected');
  });
  const selectedBtn = document.getElementById(selectedId);
  if (selectedBtn) {
    selectedBtn.classList.add('race-option-selected');
  }
}

/**
 * Show modal with given HTML
 * @param {string} html - Modal HTML content
 */
function showModal(html) {
  const container = document.getElementById('modal-container');
  container.innerHTML = html;

  // Add click handler to close on backdrop click
  const backdrop = document.getElementById('modal-backdrop');
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) {
      closeModal();
    }
  });

  // Add ESC key handler
  document.addEventListener('keydown', handleEscKey);
}

/**
 * Close the modal
 */
export function closeModal() {
  const container = document.getElementById('modal-container');
  container.innerHTML = '';
  currentModal = null;
  currentTrack = null;
  document.removeEventListener('keydown', handleEscKey);
}

/**
 * Handle ESC key press
 * @param {KeyboardEvent} e - Keyboard event
 */
function handleEscKey(e) {
  if (e.key === 'Escape') {
    closeModal();
  }
}

/**
 * Toggle video visibility
 * @param {string} videoId - The video container ID
 */
function toggleVideo(videoId) {
  const videoContainer = document.getElementById(videoId);
  const icon = document.getElementById(`${videoId}-icon`);
  const text = document.getElementById(`${videoId}-text`);

  if (videoContainer.classList.contains('video-hidden')) {
    videoContainer.classList.remove('video-hidden');
    icon.textContent = '▼';
    text.textContent = 'Hide Video';
  } else {
    videoContainer.classList.add('video-hidden');
    icon.textContent = '▶';
    text.textContent = 'Show Video';
  }
}

/**
 * Toggle tweet visibility
 * @param {string} tweetId - The tweet container ID
 */
function toggleTweet(tweetId) {
  const tweetContainer = document.getElementById(tweetId);
  const icon = document.getElementById(`${tweetId}-icon`);
  const text = document.getElementById(`${tweetId}-text`);

  if (tweetContainer.classList.contains('hidden')) {
    tweetContainer.classList.remove('hidden');
    icon.textContent = '▼';
    text.textContent = 'Hide Video';

    // Load Twitter widget script if not already loaded
    if (!window.twttr) {
      const script = document.createElement('script');
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      document.body.appendChild(script);
    } else {
      // Reprocess tweets if script already loaded
      window.twttr.widgets.load();
    }
  } else {
    tweetContainer.classList.add('hidden');
    icon.textContent = '▶';
    text.textContent = 'Show Video';
  }
}

/**
 * Handle mobile dropdown race selection
 * @param {string} value - Selected value ('standard' or intermission ID)
 * @param {string} trackId - Track ID
 */
function handleMobileRaceChange(value, trackId) {
  if (value === 'standard') {
    selectRace(trackId, 'standard');
  } else {
    selectRace(trackId, 'intermission', value);
  }
}

/**
 * Toggle inherited shortcuts visibility
 */
function toggleInheritedShortcuts() {
  const container = document.getElementById('inherited-shortcuts');
  const icon = document.getElementById('inherited-shortcuts-icon');

  if (container.classList.contains('hidden')) {
    container.classList.remove('hidden');
    icon.textContent = '▲';
  } else {
    container.classList.add('hidden');
    icon.textContent = '▼';
  }
}

// Expose functions to window for onclick handlers
window.closeModal = closeModal;
window.selectRace = selectRace;
window.toggleVideo = toggleVideo;
window.toggleTweet = toggleTweet;
window.handleMobileRaceChange = handleMobileRaceChange;
window.toggleInheritedShortcuts = toggleInheritedShortcuts;
